// Documentary Analyzer Service

export interface DocumentaryScene {
  sceneNumber: number;
  title: string;
  description: string;
  duration: string;
  imagePrompt: string;
  videoPrompt: string;
  cameraWork: string;
  mood: string;
  soundscape: string;
}

export interface DocumentaryStyle {
  genre: 'comic' | 'scientific' | 'historical' | 'nature' | 'biographical' | 'investigative' | 'cultural';
  visualStyle: string;
  colorPalette: string;
  cameraStyle: string;
  pacing: string;
  narrativeTone: string;
}

const genreStyles: Record<string, DocumentaryStyle> = {
  comic: {
    genre: 'comic',
    visualStyle: 'comic book art style, cel shaded, bold outlines, vibrant colors',
    colorPalette: 'bright saturated colors, high contrast, pop art palette',
    cameraStyle: 'dynamic angles, dramatic perspectives, comic panel compositions',
    pacing: 'fast-paced, energetic cuts',
    narrativeTone: 'humorous, lighthearted, entertaining'
  },
  scientific: {
    genre: 'scientific',
    visualStyle: 'hyperrealistic, ultra detailed, scientific accuracy, 8K resolution',
    colorPalette: 'clean whites, technological blues, precise colors',
    cameraStyle: 'steady shots, microscopic details, time-lapse, slow motion',
    pacing: 'measured, informative',
    narrativeTone: 'educational, precise, authoritative'
  },
  historical: {
    genre: 'historical',
    visualStyle: 'period accurate, cinematic quality, historically authentic',
    colorPalette: 'desaturated tones, sepia hints, period-appropriate colors',
    cameraStyle: 'epic wide shots, documentary style, archival footage aesthetic',
    pacing: 'deliberate, contemplative',
    narrativeTone: 'serious, respectful, informative'
  },
  nature: {
    genre: 'nature',
    visualStyle: 'BBC Planet Earth style, ultra high definition, natural lighting',
    colorPalette: 'natural earth tones, vibrant greens, sky blues',
    cameraStyle: 'wildlife cinematography, aerial shots, macro photography',
    pacing: 'patient, observational',
    narrativeTone: 'awe-inspiring, educational, environmental'
  },
  biographical: {
    genre: 'biographical',
    visualStyle: 'intimate portraits, personal moments, authentic environments',
    colorPalette: 'warm human tones, soft lighting, emotional colors',
    cameraStyle: 'close-ups, handheld moments, interview setups',
    pacing: 'narrative-driven, emotional beats',
    narrativeTone: 'personal, inspiring, human'
  },
  investigative: {
    genre: 'investigative',
    visualStyle: 'gritty realism, documentary noir, high contrast',
    colorPalette: 'dark shadows, stark contrasts, moody lighting',
    cameraStyle: 'handheld camera, hidden camera aesthetic, surveillance style',
    pacing: 'tense, suspenseful',
    narrativeTone: 'serious, probing, revelatory'
  },
  cultural: {
    genre: 'cultural',
    visualStyle: 'vibrant cultural authenticity, traditional aesthetics',
    colorPalette: 'rich cultural colors, traditional patterns, festive hues',
    cameraStyle: 'immersive shots, cultural ceremonies, community gatherings',
    pacing: 'rhythmic, ceremonial',
    narrativeTone: 'respectful, celebratory, educational'
  }
};

export const detectDocumentaryGenre = (story: string): DocumentaryStyle => {
  const storyLower = story.toLowerCase();
  
  // Keywords for genre detection
  const genreKeywords = {
    comic: ['comic', 'funny', 'humor', 'cartoon', 'animated', 'comedy', 'satire', 'parody'],
    scientific: ['science', 'research', 'experiment', 'discovery', 'technology', 'physics', 'chemistry', 'biology', 'space'],
    historical: ['history', 'war', 'ancient', 'medieval', 'revolution', 'empire', 'civilization', 'past'],
    nature: ['nature', 'wildlife', 'animal', 'ocean', 'forest', 'ecosystem', 'environment', 'planet'],
    biographical: ['life', 'person', 'biography', 'story of', 'journey', 'portrait', 'individual'],
    investigative: ['investigate', 'uncover', 'expose', 'truth', 'scandal', 'corruption', 'mystery'],
    cultural: ['culture', 'tradition', 'festival', 'ceremony', 'customs', 'heritage', 'community']
  };

  // Count keyword matches for each genre
  let bestMatch = 'scientific'; // default
  let maxMatches = 0;

  for (const [genre, keywords] of Object.entries(genreKeywords)) {
    const matches = keywords.filter(keyword => storyLower.includes(keyword)).length;
    if (matches > maxMatches) {
      maxMatches = matches;
      bestMatch = genre;
    }
  }

  return genreStyles[bestMatch];
};

export const generateDocumentaryScenes = (
  story: string,
  style: DocumentaryStyle,
  platform: 'midjourney' | 'veo' | 'universal' = 'midjourney'
): DocumentaryScene[] => {
  // Parse story into logical scenes
  const sentences = story.split(/[.!?]+/).filter(s => s.trim().length > 0);
  const scenesCount = Math.min(Math.max(3, Math.ceil(sentences.length / 2)), 10);
  const scenes: DocumentaryScene[] = [];

  for (let i = 0; i < scenesCount; i++) {
    const sceneContent = sentences.slice(
      i * Math.ceil(sentences.length / scenesCount),
      (i + 1) * Math.ceil(sentences.length / scenesCount)
    ).join('. ');

    scenes.push(generateScene(i + 1, sceneContent, style, platform));
  }

  return scenes;
};

const generateScene = (
  sceneNumber: number,
  content: string,
  style: DocumentaryStyle,
  platform: string
): DocumentaryScene => {
  const sceneTypes = ['establishing', 'detail', 'action', 'emotional', 'transitional'];
  const sceneType = sceneTypes[sceneNumber % sceneTypes.length];
  
  // Camera work based on scene type
  const cameraWork = {
    establishing: 'wide establishing shot, aerial view, sweeping camera movement',
    detail: 'close-up shot, macro lens, focus pull, intimate framing',
    action: 'tracking shot, dynamic movement, following action',
    emotional: 'slow zoom, handheld camera, emotional close-up',
    transitional: 'smooth transition, fade between scenes, time-lapse'
  }[sceneType];

  // Generate prompts based on platform
  let imagePrompt = '';
  let videoPrompt = '';

  if (platform === 'midjourney') {
    imagePrompt = `${content}, ${style.visualStyle}, ${style.colorPalette}, ${cameraWork}, professional documentary photography --ar 16:9 --v 7 --style raw --q 2`;
  } else {
    imagePrompt = `${content}, ${style.visualStyle}, ${style.colorPalette}, ${cameraWork}, documentary cinematography, 8K resolution`;
  }

  if (platform === 'veo') {
    videoPrompt = `${content}, ${cameraWork}, ${style.pacing}, ${style.visualStyle}, documentary film style, smooth camera movement, 10 seconds`;
  } else {
    videoPrompt = `Video: ${content}, ${style.cameraStyle}, ${style.pacing}, professional documentary footage`;
  }

  return {
    sceneNumber,
    title: `Scene ${sceneNumber}: ${sceneType.charAt(0).toUpperCase() + sceneType.slice(1)}`,
    description: content,
    duration: sceneType === 'establishing' ? '5-8 seconds' : '3-5 seconds',
    imagePrompt,
    videoPrompt,
    cameraWork: cameraWork || '',
    mood: getMoodForScene(sceneType, style.genre),
    soundscape: getSoundscapeForScene(sceneType, style.genre)
  };
};

const getMoodForScene = (sceneType: string, genre: string): string => {
  const moodMap: Record<string, Record<string, string>> = {
    comic: {
      establishing: 'playful and energetic',
      detail: 'quirky and amusing',
      action: 'fast-paced and hilarious',
      emotional: 'heartwarming and funny',
      transitional: 'lighthearted'
    },
    scientific: {
      establishing: 'wonder and discovery',
      detail: 'precise and fascinating',
      action: 'breakthrough moment',
      emotional: 'awe-inspiring',
      transitional: 'contemplative'
    },
    historical: {
      establishing: 'epic and grand',
      detail: 'authentic period feel',
      action: 'dramatic historical moment',
      emotional: 'poignant and meaningful',
      transitional: 'passage of time'
    }
  };

  return moodMap[genre]?.[sceneType] || 'atmospheric and engaging';
};

const getSoundscapeForScene = (sceneType: string, genre: string): string => {
  const soundMap: Record<string, string> = {
    comic: 'upbeat music, cartoon effects, comedic timing',
    scientific: 'ambient technology sounds, subtle electronic music',
    historical: 'period-appropriate music, orchestral score',
    nature: 'natural ambient sounds, minimal music',
    biographical: 'emotional orchestral, interview audio',
    investigative: 'tense underscore, dramatic reveals',
    cultural: 'traditional music, authentic sounds'
  };

  return soundMap[genre] || 'professional documentary score';
};

export const generateStoryboard = (
  scenes: DocumentaryScene[]
): string => {
  let storyboard = '📽️ **DOCUMENTARY STORYBOARD**\n\n';
  
  scenes.forEach(scene => {
    storyboard += `**${scene.title}**\n`;
    storyboard += `Duration: ${scene.duration}\n`;
    storyboard += `Camera: ${scene.cameraWork}\n`;
    storyboard += `Mood: ${scene.mood}\n`;
    storyboard += `Sound: ${scene.soundscape}\n\n`;
    storyboard += `Image Prompt:\n\`\`\`\n${scene.imagePrompt}\n\`\`\`\n\n`;
    storyboard += `Video Prompt:\n\`\`\`\n${scene.videoPrompt}\n\`\`\`\n\n`;
    storyboard += '---\n\n';
  });

  return storyboard;
};